import React, { useMemo } from "react";

const sections = [
  {
    title: "Overview",
    content: [
      "The PEG Layout Tool helps you sketch photovoltaic (PV) layouts, evaluate energy yield, and build quick commercial quotes in one place.",
      "Use the Design workspace to outline project boundaries, place PEG blocks, and inspect site metrics in real time.",
      "Switch to the Financial and Quote workspaces to refine cost assumptions, review cashflow metrics, and prepare customer-ready summaries.",
    ],
  },
  {
    title: "Quick start",
    content: [
      "Zoom and pan the base map to locate your site. Hold Shift while dragging to rotate the map.",
      "Use the drawing tools in the lower-left corner to trace the project boundary or import a KMZ file from your GIS workflow.",
      "Populate the Project panel with a project name and download the JSON file frequently to version your work.",
    ],
  },
  {
    title: "Design workspace",
    content: [
      "Project panel: Download, export, or load project files. Downloads include layout geometry, financial assumptions, and quote selections.",
      "Polygon vertices: Edit latitude/longitude pairs manually. Enable Auto-fit to snap the layout to the edited boundary.",
      "Layout setup: Select the mounting system (PEG, fixed, or tracker), module preset, and orientation. Configure row and column gaps, block dimensions, and aisle spacing.",
      "Optimizer: Explore auto-generated layouts based on ground coverage ratio (GCR) or estimated annual yield. Lock dimensions or module presets when needed.",
      "Metrics panel: Review live KPIs including module count, DC size, density, ground coverage ratio, site area, annual yield, and cost-per-kW outputs.",
      "Map controls: Use the Leaflet toolbar to add rings, move vertices, or delete shapes. Hold Alt while dragging a vertex to keep orthogonal edges aligned.",
    ],
  },
  {
    title: "Financial workspace",
    content: [
      "Cost tree: Expand each cost category to toggle individual line items in or out of the financial model.",
      "Per-kWp adjustments: Enter updated per-kWp values or custom totals where required. All edits immediately update the KPIs at the top of the page.",
      "Financial assumptions: Configure inflation, discount rate, debt ratio, interest rate, and project lifetime to compute LCOE and CAPEX summaries.",
      "Currency selector: Switch between USD, EUR, and other supported currencies without leaving the page.",
    ],
  },
  {
    title: "Quote workspace",
    content: [
      "Pricing summary: View subtotal, taxes, and grand total values based on the selections in the Financial workspace.",
      "Quote notes: Add customer-facing notes such as delivery timelines, warranty highlights, or scope clarifications.",
      "Export options: Use the Download button in the Project panel to capture a bundled JSON of the quote together with the layout.",
    ],
  },
  {
    title: "File management",
    content: [
      "JSON download: Saves the entire project, including geometry, system configuration, and financial parameters.",
      "KMZ export: Generates a KMZ with PEG block footprints for review in Google Earth or GIS suites.",
      "Load project: Import a previously saved JSON to restore the full session state, including manual edits to costs and optimizer locks.",
    ],
  },
  {
    title: "Keyboard and mouse shortcuts",
    content: [
      "Scroll wheel to zoom. Hold Shift + drag to rotate the map view.",
      "Hold Control/Command while dragging a vertex to duplicate a point for detailed boundary adjustments.",
      "Press Delete while a vertex is selected to remove it from the polygon.",
    ],
  },
  {
    title: "Troubleshooting",
    content: [
      "Layout not visible: Ensure the polygon is closed and contains at least three vertices. Auto-fit can help align the design region to your edits.",
      "Unexpected GCR or density: Verify gap settings, block dimensions, and module orientation. Use the Optimizer to iterate through viable combinations.",
      "Slow performance in large sites: Hide the Optimizer panel and zoom closer to reduce on-screen geometry when editing very large boundaries.",
    ],
  },
  {
    title: "Support",
    content: [
      "Send project JSON files and a brief description of the issue to the engineering team for review.",
      "Include KMZ exports when reporting alignment problems or coordinate discrepancies.",
    ],
  },
];

function buildPdfLines() {
  const lines = [];
  lines.push("PEG Layout Tool – User Manual");
  lines.push("");
  sections.forEach((section) => {
    lines.push(section.title);
    section.content.forEach((paragraph) => {
      lines.push(paragraph);
    });
    lines.push("");
  });
  return lines;
}

const UserManual = () => {
  const pdfLines = useMemo(() => buildPdfLines(), []);
  const sectionTitles = useMemo(
    () => new Set(sections.map((section) => section.title)),
    []
  );

  const downloadPdf = async () => {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const leftMargin = 54;
    const topMargin = 64;
    const lineHeight = 18;
    let cursorY = topMargin;

    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("PEG Layout Tool – User Manual", leftMargin, cursorY);
    cursorY += lineHeight * 1.5;

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    const pageHeight = doc.internal.pageSize.getHeight();
    const maxWidth = doc.internal.pageSize.getWidth() - leftMargin * 2;

    pdfLines.slice(1).forEach((line) => {
      if (line === "") {
        cursorY += lineHeight * 0.75;
        return;
      }

      if (sectionTitles.has(line)) {
        if (cursorY + lineHeight * 2 > pageHeight - topMargin) {
          doc.addPage();
          cursorY = topMargin;
        }
        doc.setFont("helvetica", "bold");
        doc.text(line, leftMargin, cursorY);
        doc.setFont("helvetica", "normal");
        cursorY += lineHeight * 1.2;
        return;
      }

      const textLines = doc.splitTextToSize(line, maxWidth);
      textLines.forEach((textLine) => {
        if (cursorY + lineHeight > pageHeight - topMargin) {
          doc.addPage();
          cursorY = topMargin;
        }
        doc.text(textLine, leftMargin, cursorY);
        cursorY += lineHeight;
      });
      cursorY += lineHeight * 0.5;
    });

    doc.save("peg-layout-tool-user-manual.pdf");
  };

  return (
    <div className="manual-page">
      <div className="manual-header">
        <h1>PEG Layout Tool – User Manual</h1>
        <button className="btn btn-primary" onClick={downloadPdf}>
          Download PDF
        </button>
      </div>
      {sections.map((section) => (
        <section key={section.title} className="manual-section">
          <h2>{section.title}</h2>
          {section.content.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </section>
      ))}
    </div>
  );
};

export default UserManual;
